
































































































































import { mixins } from "vue-class-component";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import FormField from "@/lib/components/FormField.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import IdGenerator from "@/lib/services/id-generator";
import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import dataStore from "@/store";

import FactoryRushCapacityProductGroup from "../../factory-rush-capacity-product-group.model";
import FactoryRushCapacitySlot from "../../factory-rush-capacity-slot.model";
import FactoryRushCapacityType from "../../factory-rush-capacity-type.model";
import FactoryRushCapacityStore from "../../store";

@Component({
  components: {
    FormErrors,
    FormField,
    Multiselect,
    SubmitButton,
  },
})
export default class FactoryRushCapacityEditForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly factoryId!: string;

  @Prop({ required: true })
  public readonly groups!: FactoryRushCapacityProductGroup[];

  @Prop({ required: true })
  public readonly configuredSlots!: FactoryRushCapacitySlot[];

  @Prop({ required: true })
  public readonly types!: FactoryRushCapacityType[];

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  public selectedGroup: FactoryRushCapacityProductGroup | null = null;

  private fCellValues: Record<string, string> = {};
  private fFactoryRushCapacityStore: FactoryRushCapacityStore;
  private fIsSaving = false;
  private fNewlyAddedGroupIds: string[] = [];
  private fStagedGroupIds: string[] = [];

  get columnWidth(): string {
    const columnsCount = this.types.length ? this.types.length : 1;
    const value = Math.round((80 * 100) / columnsCount) / 100;

    return value.toString() + "%";
  }

  get hasMatrixData(): boolean {
    return this.types.length > 0;
  }

  get eligibleGroups(): FactoryRushCapacityProductGroup[] {
    return this.groups.filter(
      (group) => this.hasMembers(group.id) && !this.isGroupStaged(group.id)
    );
  }

  get visibleGroups(): FactoryRushCapacityProductGroup[] {
    return this.fStagedGroupIds
      .map((stagedGroupId) =>
        this.groups.find(
          (group) => group.id.toString() === stagedGroupId.toString()
        )
      )
      .filter(
        (group): group is FactoryRushCapacityProductGroup => group !== undefined
      );
  }

  constructor() {
    super();

    this.fFactoryRushCapacityStore = getModule(
      FactoryRushCapacityStore,
      dataStore
    );
  }

  public getCellValue(groupId: string, typeId: string): string {
    return this.fCellValues[this.getCellKey(groupId, typeId)] || "";
  }

  public getInputClasses(
    groupId: string,
    typeId: string
  ): Record<string, boolean> {
    const value = this.getCellValue(groupId, typeId);
    const isPositiveCapacity = /^\d+$/.test(value.trim()) && +value > 0;

    return {
      "-not-empty": isPositiveCapacity,
    };
  }

  public onCellInput(groupId: string, typeId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.$set(this.fCellValues, this.getCellKey(groupId, typeId), target.value);
  }

  public addGroup(): void {
    const selectedGroup = this.selectedGroup;

    if (
      !selectedGroup ||
      this.isDisabled ||
      !this.eligibleGroups.some((group) => group.id === selectedGroup.id)
    ) {
      return;
    }

    this.fStagedGroupIds.push(selectedGroup.id);

    if (!this.fNewlyAddedGroupIds.includes(selectedGroup.id)) {
      this.fNewlyAddedGroupIds.push(selectedGroup.id);
    }

    this.selectedGroup = null;
  }

  public removeGroup(groupId: string): void {
    if (this.isDisabled) {
      return;
    }

    this.fStagedGroupIds = this.fStagedGroupIds.filter(
      (stagedGroupId) => stagedGroupId !== groupId
    );
  }

  public onCancelClick(): void {
    if (this.isDisabled) {
      return;
    }

    this.$emit("cancel");
  }

  protected async performSubmit(): Promise<void> {
    if (!this.hasMatrixData) {
      throw new Error(
        "Rush capacity groups or types are not available. Reload the page and try again."
      );
    }

    const slots: FactoryRushCapacitySlot[] = [];

    this.visibleGroups.forEach((group) => {
      this.types.forEach((type) => {
        const rawValue = this.getCellValue(group.id, type.id).trim();

        if (!rawValue) {
          return;
        }

        if (!/^\d+$/.test(rawValue)) {
          throw new Error(
            "Capacity values must be positive integers or 0. Use 0 or leave a cell blank to clear it."
          );
        }

        const capacity = +rawValue;

        if (capacity === 0) {
          return;
        }

        const existingSlot = this.getConfiguredSlot(group.id, type.id);

        const slot = new FactoryRushCapacitySlot(
          existingSlot ? existingSlot.id : this.fIdGenerator.getId(),
          this.factoryId,
          group.id,
          type.id,
          capacity
        );

        slot.isNew = existingSlot == null;

        slots.push(slot);
      });
    });

    const emptyNewGroup = this.fNewlyAddedGroupIds.find(
      (groupId) =>
        this.isGroupStaged(groupId) &&
        !slots.some((slot) => slot.productGroup === groupId)
    );

    if (emptyNewGroup) {
      throw new Error(
        "Each newly added group must have at least one positive capacity."
      );
    }

    this.fIsSaving = true;

    try {
      const savedSlots = await this.fFactoryRushCapacityStore.saveFactoryRushCapacitySlotsByFactoryId(
        {
          factoryId: this.factoryId,
          slots,
        }
      );

      this.syncDraftFromSlots(savedSlots);
      this.$emit("saved", savedSlots);
    } finally {
      await this.$nextTick();
      this.fIsSaving = false;
    }
  }

  private getCellKey(groupId: string, typeId: string): string {
    return `${groupId}:${typeId}`;
  }

  private getConfiguredSlot(
    groupId: string,
    typeId: string
  ): FactoryRushCapacitySlot | undefined {
    return this.configuredSlots.find(
      (slot) =>
        slot.productGroup.toString() === groupId.toString() &&
        slot.type.toString() === typeId.toString()
    );
  }

  private hasMembers(groupId: string): boolean {
    return this.fFactoryRushCapacityStore.hasFactoryRushCapacityProductGroupMembers(
      groupId
    );
  }

  private isGroupStaged(groupId: string): boolean {
    return this.fStagedGroupIds.some(
      (stagedGroupId) => stagedGroupId.toString() === groupId.toString()
    );
  }

  private getConfiguredGroupIds(
    configuredSlots: FactoryRushCapacitySlot[]
  ): string[] {
    return this.groups
      .filter(
        (group) =>
          this.hasMembers(group.id) &&
          configuredSlots.some(
            (slot) => slot.productGroup.toString() === group.id.toString()
          )
      )
      .map((group) => group.id);
  }

  private initializeDraft(): void {
    this.fStagedGroupIds = this.getConfiguredGroupIds(this.configuredSlots);
    this.fNewlyAddedGroupIds = [];
    this.selectedGroup = null;
    this.syncCellValues();
  }

  private syncDraftFromSlots(configuredSlots: FactoryRushCapacitySlot[]): void {
    this.fStagedGroupIds = this.getConfiguredGroupIds(configuredSlots);
    this.fNewlyAddedGroupIds = [];
    this.selectedGroup = null;
    this.syncCellValues(configuredSlots);
  }

  private syncCellValues(
    configuredSlots: FactoryRushCapacitySlot[] = this.configuredSlots
  ): void {
    const values: Record<string, string> = {};

    configuredSlots.forEach((slot) => {
      values[
        this.getCellKey(slot.productGroup, slot.type)
      ] = slot.capacity.toString();
    });

    this.fCellValues = values;
  }

  @Watch("groups", { immediate: true })
  private _onGroupsChange(): void {
    if (this.fIsSaving) {
      return;
    }

    this.initializeDraft();
  }

  @Watch("configuredSlots", { immediate: true })
  private _onConfiguredSlotsChange(): void {
    if (this.fIsSaving) {
      return;
    }

    this.initializeDraft();
  }
}
